import styled from "styled-components";

import { color, radius } from "../../../styles/tokens";

/* A real <img>, which is the whole point.
 *
 * Every photograph on /hebe used to be a CSS background-image on a <div> — the hero, the About
 * column, the gallery cells and the room cards. That made all of them invisible to assistive
 * technology and to image search, impossible to give an alt, and impossible to serve at anything
 * but full resolution: the gallery loaded originals into 313px cells.
 *
 * `source` accepts either a bare URL string (what the Strapi services returned before this work) or
 * an object carrying Strapi's derived `formats`, in which case a srcset is built from them.
 */

const Frame = styled.div`
  position: relative;
  overflow: hidden;
  background: ${color.canvasAlt};
  border-radius: ${(props) => props.$radius};
  ${(props) => (props.$ratio ? `aspect-ratio: ${props.$ratio};` : "")}

  /* Holds the box before the image decodes, so a late-loading photo cannot shift the page. */
  img {
    width: 100%;
    height: 100%;
    object-fit: ${(props) => props.$fit};
    object-position: ${(props) => props.$position};
  }
`;

/* Strapi returns { thumbnail, small, medium, large }, each { url, width }. Any subset may be
 * missing — a small upload gets no `large` — so filter before building the descriptor list.
 */
const buildSrcSet = (source) => {
  if (!source || typeof source === "string" || !source.formats)
    return undefined;

  const candidates = Object.values(source.formats)
    .filter((format) => format && format.url && format.width)
    .map((format) => `${format.url} ${format.width}w`);

  if (source.url && source.width)
    candidates.push(`${source.url} ${source.width}w`);

  return candidates.length > 1 ? candidates.join(", ") : undefined;
};

const resolveUrl = (source) => {
  if (!source) return null;
  return typeof source === "string" ? source : source.url || null;
};

const Media = (props) => {
  const url = resolveUrl(props.source);
  if (!url) return null;

  const srcSet = buildSrcSet(props.source);
  // A decorative image takes alt="" so a screen reader skips it rather than announcing a filename.
  const alt =
    props.alt !== undefined
      ? props.alt
      : typeof props.source === "string"
        ? ""
        : props.source.alt_text || "";

  return (
    <Frame
      className={props.className}
      $ratio={props.ratio}
      $fit={props.fit || "cover"}
      $position={props.position || "center"}
      $radius={props.radius || radius.none}
    >
      <img
        src={url}
        srcSet={srcSet}
        sizes={srcSet ? props.sizes || "100vw" : undefined}
        alt={alt}
        // Exactly one image per page should be eager: the LCP one. Everything else waits.
        loading={props.priority ? "eager" : "lazy"}
        fetchPriority={props.priority ? "high" : undefined}
        decoding={props.priority ? "sync" : "async"}
        width={
          typeof props.source === "object" ? props.source.width : undefined
        }
        height={
          typeof props.source === "object" ? props.source.height : undefined
        }
      />
    </Frame>
  );
};

export default Media;
